/**
 * UsersPage Component (TASK-049)
 *
 * Main page for user management with list view,
 * invite dialog, and user actions.
 *
 * KAN-752: "Редактировать" ведёт на страницу оператора /users/:id; кнопка
 * "Новая кампания" (users.manage) создаёт оператора-кампанию без входа.
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  UserPlus,
  Edit,
  UserX,
  UserCheck,
  Key,
  Search,
  Loader2,
  Users,
  AlertCircle,
  MoreHorizontal,
  Megaphone,
} from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { InviteUserDialog } from '@/components/users/InviteUserDialog';
import { CreateCampaignDialog } from '@/components/users/CreateCampaignDialog';
import { PaginationBar } from '@/components/common/PaginationBar';
import { usePageSize } from '@/hooks/usePageSize';
import { useDebounce } from '@/hooks/useDebounce';
import { usersApi } from '@/services/api/users';
import { apiClient } from '@/services/api/client';
import type { User, UserRole, UserFilters } from '@/types/user';

// Role badge variant mapping
const ROLE_BADGE_VARIANTS: Record<UserRole, 'destructive' | 'default' | 'outline'> = {
  OWNER: 'default',
  USER: 'outline',
};

// Role display names
const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  OWNER: 'Владелец',
  USER: 'Пользователь',
};

export function UsersPage(): JSX.Element {
  const queryClient = useQueryClient();

  // Current user — to determine if OWNER
  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const res = await apiClient.get<User>('/api/v1/auth/me/');
      return res.data;
    },
  });
  const isOwner = currentUser?.role === 'OWNER';
  // KAN-752: кампании-операторы создаёт тот, кто управляет пользователями.
  const canManage = currentUser?.permissions?.users?.manage === true;
  const navigate = useNavigate();

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const { page: currentPage, setPage: setCurrentPage, pageSize, setPageSize } = usePageSize('users');
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isCampaignDialogOpen, setIsCampaignDialogOpen] = useState(false);
  const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState<User | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // Debounce search query
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Set page title
  useEffect(() => {
    document.title = 'Пользователи - SMETA AI';
  }, []);

  // Build filters
  const filters: UserFilters = useMemo(() => {
    const result: UserFilters = {
      page: currentPage,
      page_size: pageSize,
    };

    if (debouncedSearch) {
      result.search = debouncedSearch;
    }
    if (roleFilter !== 'all') {
      result.role = roleFilter;
    }
    if (statusFilter !== 'all') {
      result.is_active = statusFilter === 'active';
    }

    return result;
  }, [debouncedSearch, roleFilter, statusFilter, currentPage, pageSize]);

  // Query users
  const {
    data: usersData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['users', filters],
    queryFn: () => usersApi.list(filters),
  });

  // Deactivate mutation
  const deactivateMutation = useMutation({
    mutationFn: (userId: string) => usersApi.deactivate(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Пользователь деактивирован');
      setIsDeactivateDialogOpen(false);
      setUserToDeactivate(null);
    },
  });

  // Reactivate mutation
  const reactivateMutation = useMutation({
    mutationFn: (userId: string) => usersApi.reactivate(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Пользователь активирован');
    },
  });

  // Password reset mutation
  const passwordResetMutation = useMutation({
    mutationFn: (userId: string) => usersApi.sendPasswordReset(userId),
    onSuccess: () => {
      toast.success('Email для сброса пароля отправлен');
      setOpenDropdown(null);
    },
  });

  // Handlers
  const handleOpenInviteDialog = useCallback(() => {
    setIsInviteDialogOpen(true);
  }, []);

  const handleCloseInviteDialog = useCallback(() => {
    setIsInviteDialogOpen(false);
  }, []);

  const handleInviteSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['users'] });
    toast.success('Приглашение отправлено');
  }, [queryClient]);

  // KAN-752: редактирование - отдельная страница оператора.
  const handleEditUser = useCallback(
    (user: User) => navigate(`/users/${user.id}`),
    [navigate]
  );

  const handleDeactivateUser = useCallback((user: User) => {
    setUserToDeactivate(user);
    setIsDeactivateDialogOpen(true);
  }, []);

  const handleConfirmDeactivate = useCallback(() => {
    if (userToDeactivate) {
      deactivateMutation.mutate(userToDeactivate.id);
    }
  }, [userToDeactivate, deactivateMutation]);

  const handleReactivateUser = useCallback(
    (user: User) => {
      reactivateMutation.mutate(user.id);
    },
    [reactivateMutation]
  );

  const handleResetPassword = useCallback(
    (user: User) => {
      passwordResetMutation.mutate(user.id);
    },
    [passwordResetMutation]
  );

  const handleRoleFilterChange = useCallback((value: string) => {
    setRoleFilter(value as UserRole | 'all');
    setCurrentPage(1);
  }, [setCurrentPage]);

  const handleStatusFilterChange = useCallback((value: string) => {
    setStatusFilter(value as 'all' | 'active' | 'inactive');
    setCurrentPage(1);
  }, [setCurrentPage]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
      setCurrentPage(1);
    },
    [setCurrentPage]
  );

  const handleToggleDropdown = useCallback((userId: string) => {
    setOpenDropdown((prev) => (prev === userId ? null : userId));
  }, []);

  // Format date
  const formatDate = useCallback((dateString: string | null) => {
    if (!dateString) return 'Никогда';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }, []);

  return (
    <>
      <PageLayout
        data-testid="users-page"
        title="Пользователи"
        subtitle={<span className="hidden sm:inline">Управление членами команды и их правами доступа</span>}
        titleIcon={
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
            <Users className="h-5 w-5 text-white" />
          </div>
        }
        titleAddon={
          <span
            data-testid="total-count"
            className="hidden sm:inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold"
          >
            {usersData?.count ?? 0}
          </span>
        }
        breadcrumbs={[
          { label: 'Главная', href: '/' },
          { label: 'Пользователи' },
        ]}
        actions={
          <div className="flex items-center gap-2 sm:gap-4">
            {canManage && (
              <Button
                variant="outline"
                data-testid="new-campaign-button"
                onClick={() => setIsCampaignDialogOpen(true)}
              >
                <Megaphone className="h-4 w-4" />
                <span className="hidden sm:inline ml-2">Новая кампания</span>
              </Button>
            )}
            {isOwner && (
              <Button
                data-testid="invite-user-button"
                onClick={handleOpenInviteDialog}
              >
                <UserPlus className="h-4 w-4" />
                <span className="hidden sm:inline ml-2">Пригласить</span>
              </Button>
            )}
          </div>
        }
      >
        {/* Filters */}
        <div className="mb-4">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            {/* Search */}
            <div className="relative w-full sm:max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Поиск пользователей..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="pl-10"
              />
            </div>

            {/* Role Filter */}
            <Select value={roleFilter} onValueChange={handleRoleFilterChange}>
              <SelectTrigger data-testid="role-filter" className="w-full sm:w-[150px]">
                <SelectValue placeholder="Все роли" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все роли</SelectItem>
                <SelectItem value="OWNER">Владелец</SelectItem>
                <SelectItem value="USER">Пользователь</SelectItem>
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
              <SelectTrigger data-testid="status-filter" className="w-full sm:w-[150px]">
                <SelectValue placeholder="Все статусы" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все статусы</SelectItem>
                <SelectItem value="active">Активные</SelectItem>
                <SelectItem value="inactive">Неактивные</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div
            data-testid="users-loading"
            className="flex items-center justify-center py-12"
          >
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        )}

        {/* Error State */}
        {isError && (
          <div
            data-testid="users-error"
            className="flex flex-col items-center justify-center py-12"
          >
            <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Ошибка загрузки пользователей
            </h3>
            <p className="text-gray-500 mb-4">
              {error instanceof Error ? error.message : 'Произошла ошибка'}
            </p>
            <Button data-testid="retry-button" onClick={() => refetch()}>
              Повторить
            </Button>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !isError && usersData?.results.length === 0 && (
          <div
            data-testid="users-empty"
            className="flex flex-col items-center justify-center py-12"
          >
            <Users className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Пользователи не найдены
            </h3>
            <p className="text-gray-500 mb-4">
              Начните с приглашения первого члена команды
            </p>
            {isOwner && <Button
              data-testid="invite-user-empty-button"
              onClick={handleOpenInviteDialog}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Пригласить
            </Button>}
          </div>
        )}

        {/* Users Table */}
        {!isLoading && !isError && usersData && usersData.results.length > 0 && (
          <>
            {/* Table (desktop) */}
            <div className="hidden md:block bg-white rounded-lg border">
              <Table aria-label="Users table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Имя</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Роль</TableHead>
                    <TableHead>Режим</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead>Последний вход</TableHead>
                    <TableHead>Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usersData.results.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {user.full_name}
                        {user.is_campaign_operator && (
                          <Badge
                            data-testid={`campaign-badge-${user.id}`}
                            variant="outline"
                            className="ml-2"
                          >
                            Кампания
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge
                          data-testid={`role-badge-${user.id}`}
                          variant={ROLE_BADGE_VARIANTS[(user.role_name || user.role) as UserRole] || 'outline'}
                        >
                          {ROLE_DISPLAY_NAMES[(user.role_name || user.role) as UserRole] ||
                            user.role_name ||
                            user.role ||
                            'Пользователь'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          data-testid={`bot-mode-badge-${user.id}`}
                          variant={user.template?.codename === 'operator' ? 'default' : 'outline'}
                        >
                          {user.template?.display_name ||
                        (user.template?.codename === 'operator' ? 'Оператор' : 'Суфлёр')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          data-testid={`status-badge-${user.id}`}
                          variant={user.is_active ? 'default' : 'outline'}
                        >
                          {user.is_active ? 'Активен' : 'Неактивен'}
                        </Badge>
                      </TableCell>
                      <TableCell data-testid={`last-login-${user.id}`}>
                        {formatDate(user.last_login)}
                      </TableCell>
                      <TableCell>
                        {isOwner ? (
                        <div className="flex items-center gap-2">
                          <Button
                            data-testid={`edit-button-${user.id}`}
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditUser(user)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>

                          {/* D2: cannot deactivate/reactivate yourself */}
                          {user.id !== currentUser?.id &&
                            (user.is_active ? (
                              <Button
                                data-testid={`deactivate-button-${user.id}`}
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeactivateUser(user)}
                              >
                                <UserX className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                data-testid={`reactivate-button-${user.id}`}
                                variant="ghost"
                                size="icon"
                                onClick={() => handleReactivateUser(user)}
                              >
                                <UserCheck className="h-4 w-4" />
                              </Button>
                            ))}

                          {/* More Actions Dropdown */}
                          <div className="relative">
                            <Button
                              data-testid={`more-actions-${user.id}`}
                              variant="ghost"
                              size="icon"
                              onClick={() => handleToggleDropdown(user.id)}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>

                            {openDropdown === user.id && (
                              <div
                                data-testid={`actions-dropdown-${user.id}`}
                                className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg border z-10"
                              >
                                <button
                                  data-testid={`reset-password-${user.id}`}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                  onClick={() => handleResetPassword(user)}
                                >
                                  <Key className="h-4 w-4 mr-2" />
                                  Сбросить пароль
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        ) : <span className="text-gray-400 text-sm">—</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Cards (mobile) */}
            <div className="space-y-3 md:hidden">
              {usersData.results.map((user) => (
                <div
                  key={user.id}
                  data-testid={`user-card-${user.id}`}
                  className="bg-white border rounded-lg p-4 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">
                      {user.full_name}
                      {user.is_campaign_operator && (
                        <Badge variant="outline" className="ml-2">
                          Кампания
                        </Badge>
                      )}
                    </span>
                    <Badge
                      data-testid={`status-badge-mobile-${user.id}`}
                      variant={user.is_active ? 'default' : 'outline'}
                    >
                      {user.is_active ? 'Активен' : 'Неактивен'}
                    </Badge>
                  </div>
                  <div className="text-sm text-gray-500">{user.email}</div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={ROLE_BADGE_VARIANTS[(user.role_name || user.role) as UserRole] || 'outline'}
                    >
                      {ROLE_DISPLAY_NAMES[(user.role_name || user.role) as UserRole] ||
                        user.role_name ||
                        user.role ||
                        'Пользователь'}
                    </Badge>
                    <Badge
                      variant={user.template?.codename === 'operator' ? 'default' : 'outline'}
                    >
                      {user.template?.display_name ||
                        (user.template?.codename === 'operator' ? 'Оператор' : 'Суфлёр')}
                    </Badge>
                  </div>
                  {isOwner && (
                    <div className="flex items-center justify-end gap-2 pt-2 border-t">
                      <Button
                        data-testid={`edit-button-mobile-${user.id}`}
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditUser(user)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {user.id !== currentUser?.id &&
                        (user.is_active ? (
                          <Button
                            data-testid={`deactivate-button-mobile-${user.id}`}
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeactivateUser(user)}
                          >
                            <UserX className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            data-testid={`reactivate-button-mobile-${user.id}`}
                            variant="ghost"
                            size="icon"
                            onClick={() => handleReactivateUser(user)}
                          >
                            <UserCheck className="h-4 w-4" />
                          </Button>
                        ))}
                      <div className="relative">
                        <Button
                          data-testid={`more-actions-mobile-${user.id}`}
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleDropdown(user.id)}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                        {openDropdown === user.id && (
                          <div
                            data-testid={`actions-dropdown-mobile-${user.id}`}
                            className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg border z-10"
                          >
                            <button
                              data-testid={`reset-password-mobile-${user.id}`}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              onClick={() => handleResetPassword(user)}
                            >
                              <Key className="h-4 w-4 mr-2" />
                              Сбросить пароль
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            {usersData && (
              <PaginationBar
                page={currentPage}
                pageSize={pageSize}
                count={usersData.count}
                hasPrevious={Boolean(usersData.previous)}
                hasNext={Boolean(usersData.next)}
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
              />
            )}
          </>
        )}
      </PageLayout>

      {/* Invite Dialog */}
      <InviteUserDialog
        open={isInviteDialogOpen}
        onClose={handleCloseInviteDialog}
        onSuccess={handleInviteSuccess}
      />

      {/* KAN-752: новая кампания-оператор */}
      <CreateCampaignDialog
        open={isCampaignDialogOpen}
        onOpenChange={setIsCampaignDialogOpen}
      />

      {/* Deactivate Confirmation Dialog */}
      <Dialog
        open={isDeactivateDialogOpen}
        onOpenChange={setIsDeactivateDialogOpen}
      >
        <DialogContent data-testid="confirm-deactivate-dialog">
          <DialogHeader>
            <DialogTitle>Деактивация пользователя</DialogTitle>
            <DialogDescription>
              Вы уверены, что хотите деактивировать {userToDeactivate?.full_name}?
              Пользователь потеряет доступ к системе.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeactivateDialogOpen(false)}
            >
              Отмена
            </Button>
            <Button
              variant="destructive"
              data-testid="confirm-deactivate-button"
              onClick={handleConfirmDeactivate}
              disabled={deactivateMutation.isPending}
            >
              {deactivateMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Деактивировать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default UsersPage;
